import './popupConfirm.less'
import { Modal } from 'semantic-ui-react'
import { X as IconX } from 'phosphor-react'
import { useIntl } from 'react-intl'
import classNames from 'classnames'
import React, { ReactElement, ReactNode } from 'react'
import { Typography, Button, Checkbox } from '@lightbeamai/design-system'

interface IProps {
  content: ReactNode
  header?: string | number | boolean | ReactElement | ReactElement[]
  confirmButton?: string
  confirmButtonTestId?: string
  cancelButton?: string
  cancelButtonTestId?: string
  contentClass?: string
  className?: string
  open?: boolean
  isProceedDisabled?: boolean
  dismissible?: boolean
  isConfirmBtnNegative?: boolean
  onConfirm?: () => void
  onCancel?: () => void
  onClose?: () => void
  onCheckbox?: () => void
  checkBoxTitle?: string
  checkBoxState?: boolean
  isLoading?: boolean
}

const PopupConfirm = ({
  content = '',
  contentClass = '',
  confirmButton,
  confirmButtonTestId = '',
  cancelButton,
  cancelButtonTestId = '',
  className = '',
  header = '',
  open = false,
  isLoading,
  dismissible = false,
  isProceedDisabled = false,
  isConfirmBtnNegative,
  onConfirm,
  onCancel,
  onClose,
  onCheckbox,
  checkBoxTitle,
  checkBoxState
}: IProps): React.ReactElement => {
  const intl = useIntl()
  const confirmButtonDefault = intl.formatMessage({ id: 'btn.okay' })
  const cancelButtonDefault = intl.formatMessage({ id: 'btn.cancel' })
  const allowClose = onClose && dismissible
  const confirmBtnText = confirmButton || confirmButtonDefault
  const cancelBtnText = cancelButton || cancelButtonDefault
  const headerContent = (
    <>
      {header}
      {allowClose && (
        <button onClick={onClose} className="btn-dismiss xs-p-0 xs-mr-0">
          <IconX size={18} color="#2f363d" />
        </button>
      )}
    </>
  )
  const handleCancel = (e) => {
    e.stopPropagation()
    e.preventDefault()
    onCancel && onCancel()
  }
  const handleConfirm = (e) => {
    e.stopPropagation()
    e.preventDefault()
    onConfirm && onConfirm()
  }

  const handleCheckboxChange = () => {
    onCheckbox && onCheckbox()
  }

  return (
    <Modal
      size="small"
      closeOnEscape={false}
      closeOnDimmerClick={false}
      open={open}
      onClose={onClose}
      className={`popup-confirm-action xs-p-0 ${className}`}
      data-test-id="popup-confirm"
    >
      {header && (
        <Typography className="header" as="span" variant="h5" color="primary">
          {headerContent}
        </Typography>
      )}
      <Modal.Content className={classNames({ [contentClass]: contentClass })}>
        <Typography as="div" color="secondary" variant="base">
          {content}
        </Typography>
      </Modal.Content>
      <Modal.Actions>
        <div>
          {onCheckbox && checkBoxTitle && (
            <Checkbox checked={checkBoxState} onChange={handleCheckboxChange}>
              {checkBoxTitle}
            </Checkbox>
          )}
        </div>
        <div className="confirm-cancel-button-container">
          {onCancel && (
            <Button
              onClick={handleCancel}
              variant="secondary"
              {...(cancelButtonTestId ? { 'data-test-id': cancelButtonTestId } : {})}
            >
              {cancelBtnText}
            </Button>
          )}
          {onConfirm && (
            <Button
              loading={isLoading}
              variant={isConfirmBtnNegative ? 'error' : 'primary'}
              onClick={handleConfirm}
              disabled={isProceedDisabled || isLoading}
              {...(confirmButtonTestId ? { 'data-test-id': confirmButtonTestId } : {})}
            >
              {confirmBtnText}
            </Button>
          )}
        </div>
      </Modal.Actions>
    </Modal>
  )
}

export default PopupConfirm
